import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';

const request = createAppRequest();

export interface CheckTaskRecord {
  uuid: string;
  taskNo: string;
  warehouseUuid: string;
  checkerMemberUuid?: string;
  checkType: string;
  scopeType: string;
  scopeSummary?: string;
  remark?: string;
  status: string;
  totalItemCount: number;
  recordedItemCount?: number;
  diffItemCount: number;
  profitItemCount: number;
  lossItemCount: number;
  totalDiffQty: string;
  totalDiffAmount: string;
  diffRate: string;
  reportSummary?: string;
  lockedByMemberUuid?: string;
  lockedAt?: string;
  startedAt?: string;
  submittedAt?: string;
  finishedAt?: string;
  createdAt: string;
}

export interface CheckItemRecord {
  uuid: string;
  taskUuid: string;
  warehouseUuid: string;
  materialUuid: string;
  stockUuid?: string;
  bookQty: string;
  unitCost: string;
  actualQty?: string;
  diffQty: string;
  diffAmount: string;
  stockFlowNo?: string;
  reason?: string;
}

export interface CreateCheckTaskPayload {
  warehouseUuid: string;
  checkerMemberUuid?: string;
  checkType?: 'full' | 'partial' | 'cycle' | 'special';
  scopeType?: 'warehouse' | 'material';
  scopeSummary?: string;
  materialUuids?: string[];
  remark?: string;
}

export interface SubmitCheckTaskPayload {
  items: Array<{
    materialUuid: string;
    actualQty: number;
    reason?: string;
  }>;
}

export type SaveCheckTaskProgressPayload = SubmitCheckTaskPayload;

export interface CheckTaskReportRecord {
  task: CheckTaskRecord;
  items: CheckItemRecord[];
  summary: {
    totalItemCount: number;
    diffItemCount: number;
    profitItemCount: number;
    lossItemCount: number;
    totalDiffQty: number;
    totalDiffAmount: number;
    diffRate: number;
    reportSummary?: string;
  };
  reasonSummary: Array<{
    reason: string;
    count: number;
    diffQty: number;
    diffAmount: number;
  }>;
}

export async function getCheckTasks(options?: ApiRequestOptions) {
  return request<CheckTaskRecord[]>('/bff/check/tasks', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getCheckTaskItems(taskUuid: string, options?: ApiRequestOptions) {
  return request<CheckItemRecord[]>(`/bff/check/tasks/${taskUuid}/items`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getDiffCheckTaskItems(taskUuid: string, options?: ApiRequestOptions) {
  return request<CheckItemRecord[]>(`/bff/check/tasks/${taskUuid}/items`, {
    method: 'GET',
    params: { diffOnly: 'true' },
    ...(options || {}),
  });
}

export async function getCheckTaskReport(taskUuid: string, options?: ApiRequestOptions) {
  return request<CheckTaskReportRecord>(`/bff/check/tasks/${taskUuid}/report`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createCheckTask(
  data: CreateCheckTaskPayload,
  options?: ApiRequestOptions,
) {
  return request<CheckTaskRecord>('/bff/check/tasks', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function startCheckTask(taskUuid: string, options?: ApiRequestOptions) {
  return request<CheckTaskRecord>(`/bff/check/tasks/${taskUuid}/start`, {
    method: 'POST',
    data: {},
    ...(options || {}),
  });
}

export async function cancelCheckTask(
  taskUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<CheckTaskRecord>(`/bff/check/tasks/${taskUuid}/cancel`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function reopenCheckTask(taskUuid: string, options?: ApiRequestOptions) {
  return request<CheckTaskRecord>(`/bff/check/tasks/${taskUuid}/reopen`, {
    method: 'POST',
    data: {},
    ...(options || {}),
  });
}

export async function submitCheckTask(
  taskUuid: string,
  data: SubmitCheckTaskPayload,
  options?: ApiRequestOptions,
) {
  return request<CheckTaskRecord>(`/bff/check/tasks/${taskUuid}/submit`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function saveCheckTaskProgress(
  taskUuid: string,
  data: SaveCheckTaskProgressPayload,
  options?: ApiRequestOptions,
) {
  return request<CheckTaskRecord>(`/bff/check/tasks/${taskUuid}/progress`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function approveCheckTask(
  taskUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<CheckTaskRecord>(`/bff/check/tasks/${taskUuid}/approve`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function rejectCheckTask(
  taskUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<CheckTaskRecord>(`/bff/check/tasks/${taskUuid}/reject`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}
